import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import { LockOutlined, CheckCircleOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MapServiceRequest } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  DESIGN TOKENS
// ===================================================================

const dt = {
  cardBg: "#FFFFFF",
  cardBorder: "#E5E7EB",
  textPrimary: "#1a1a2e",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  blue: "var(--accent, #3498DB)",
  blueHover: "var(--accent-hover, #2980B9)",
  teal: "#2AA198",
  red: "#E74C3C",
  radiusSm: "8px",
  font: 'var(--font-sans)',
  fontMono: 'var(--font-mono)',
};

// ===================================================================
//  PRIORITY HELPERS
// ===================================================================

// Priority codes: 1=urgent, 2=high, 4=low
const priorityConfig: Record<number, { label: string; bg: string; text: string; border: string; accentColor: string }> = {
  1: { label: "urgent", bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1", accentColor: dt.red },
  2: { label: "priorityHigh", bg: "#FFF0EB", text: "#E67E22", border: "#F5CBA7", accentColor: dt.red },
  4: { label: "priorityLow", bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6", accentColor: dt.teal },
};

// ===================================================================
//  SERVICE TYPE LABELS
// ===================================================================

const serviceTypeKeys: Record<number, string> = {
  1: "serviceTypeRepair",
  2: "serviceTypeParts",
  3: "serviceTypeMaintenance",
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface MapRequestCardProps {
  request: MapServiceRequest;
  currentRequestId?: string;
  onCurrentRequestClick?: () => void;
}

const MapRequestCard: React.FC<MapRequestCardProps> = ({ request, currentRequestId, onCurrentRequestClick }) => {
  const { translate } = useTranslation(["serviceBoard", "servicing"]);
  const navigate = useNavigate();

  const priority = priorityConfig[request.priorityTypeCode] || priorityConfig[4];
  const isUrgentOrHigh = request.priorityTypeCode <= 2;

  const formattedDate = request.openDate
    ? dayjs(request.openDate).format("DD MMM YYYY")
    : dayjs(request.createdDate).format("DD MMM YYYY");

  const serviceTypeLabel = serviceTypeKeys[request.serviceTypeCode]
    ? translate(serviceTypeKeys[request.serviceTypeCode])
    : "";

  const machineLabel = [request.machine.brandName, request.machine.model]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${isUrgentOrHigh ? dt.red : dt.teal}`,
        padding: "14px",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        cursor: "default",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Row 1: Company logo + name (most prominent) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          mb: "8px",
        }}
      >
        {request.customer.logoUrl ? (
          <Box
            component="img"
            src={request.customer.logoUrl}
            alt=""
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 700,
              color: dt.textSecondary,
            }}
          >
            {request.customer.name?.charAt(0)?.toUpperCase() || "?"}
          </Box>
        )}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 700,
            color: dt.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {request.customer.name}
        </Box>
      </Box>

      {/* Row 2: Machine brand + model */}
      {machineLabel && (
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            color: dt.textSecondary,
            mb: "6px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {translate("machineNumber")}: {machineLabel}
        </Box>
      )}

      {/* Row 3: Problem/Issue text (truncated) */}
      <Tooltip title={request.issue} enterDelay={400} arrow>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 400,
            color: dt.textPrimary,
            mb: "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {request.issue}
        </Box>
      </Tooltip>

      {/* Row 4: Date chip + Service type chip + Priority chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
          mb: "8px",
        }}
      >
        <DetailChip label={formattedDate} />
        {serviceTypeLabel && <DetailChip label={serviceTypeLabel} />}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: "8px",
            py: "2px",
            borderRadius: "4px",
            backgroundColor: priority.bg,
            border: `1px solid ${priority.border}`,
            fontFamily: dt.font,
            fontSize: "11px",
            fontWeight: 600,
            color: priority.text,
            lineHeight: 1.3,
          }}
        >
          {translate(priority.label)}
        </Box>
        {request.offersCount > 0 && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              color: dt.textMuted,
              ml: "auto",
            }}
          >
            {translate("sidebarOffersCount", { count: request.offersCount })}
          </Box>
        )}
      </Box>

      {/* Row 5: RFS number (small, muted, bottom) + Direct badge */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "10px",
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 500,
            color: dt.textMuted,
            letterSpacing: "-0.3px",
          }}
        >
          {request.internalNumber}
        </Box>
        {request.isDirectToMe && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              backgroundColor: "#E8F8F5",
              color: "#27AE60",
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: dt.font,
              borderRadius: "10px",
              padding: "1px 6px",
              whiteSpace: "nowrap",
              lineHeight: "16px",
            }}
          >
            <LockOutlined sx={{ fontSize: "11px" }} />
            {translate("servicing:badge.direct")}
          </Box>
        )}
        {request.hasMyOffer && !request.isOwnCompany && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              backgroundColor: "#E8F8F5",
              color: "#2AA198",
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid #A3E4D7",
              borderRadius: "10px",
              padding: "1px 6px",
              whiteSpace: "nowrap",
              lineHeight: "16px",
            }}
          >
            <CheckCircleOutline sx={{ fontSize: "11px" }} />
            {translate("offerSent")}
          </Box>
        )}
      </Box>

      {/* Row 6: Action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {!request.isOwnCompany && (
          <Button
            variant={request.hasMyOffer ? "outlined" : "contained"}
            size="small"
            onClick={() => navigate(`/service-offers/new/${request.id}`)}
            sx={{
              textTransform: "none",
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "4px",
              px: "12px",
              py: "4px",
              boxShadow: "none",
              ...(request.hasMyOffer
                ? {
                    color: "#2AA198",
                    borderColor: "#2AA198",
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: "#239088",
                      color: "#239088",
                      backgroundColor: "rgba(42,161,152,0.06)",
                    },
                  }
                : {
                    backgroundColor: dt.blue,
                    "&:hover": {
                      backgroundColor: dt.blueHover,
                      boxShadow: "none",
                    },
                  }),
            }}
          >
            {request.hasMyOffer ? translate("sendAnotherOffer") : translate("sidebarMakeOffer")}
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (request.id === currentRequestId && onCurrentRequestClick) {
              onCurrentRequestClick();
            } else {
              navigate(`/service-board/${request.id}`);
            }
          }}
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 600,
            color: request.id === currentRequestId ? dt.teal : dt.blue,
            borderColor: request.id === currentRequestId ? dt.teal : dt.blue,
            borderRadius: "4px",
            px: "12px",
            py: "4px",
            "&:hover": {
              borderColor: request.id === currentRequestId ? dt.teal : dt.blueHover,
              backgroundColor: request.id === currentRequestId
                ? "rgba(42,161,152,0.06)"
                : "rgba(52,152,219,0.04)",
            },
          }}
        >
          {request.id === currentRequestId
            ? translate("servicing:rfsDetail.currentlyViewing")
            : translate("sidebarDetails")}
        </Button>
      </Box>
    </Box>
  );
};

// ===================================================================
//  DETAIL CHIP (inline helper)
// ===================================================================

const DetailChip: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "6px",
      py: "2px",
      borderRadius: "4px",
      backgroundColor: "#F3F4F6",
      fontFamily: 'var(--font-sans)',
      fontSize: "11px",
      color: "#6B7280",
      whiteSpace: "nowrap",
      lineHeight: 1.3,
    }}
  >
    {label}
  </Box>
);

export default React.memo(MapRequestCard);
